import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useCart from '../../hooks/useCart';
import { useFormatCurrency } from '../../utils/formatCurrency';

const CartAddedDrawer = () => {
  const { t } = useTranslation();
  const formatCurrency = useFormatCurrency();
  const {
    cartFeedback,
    closeCartAddedFeedback,
    getCartItemCount,
    getCartSubtotal,
  } = useCart();

  const { isOpen, item, message, itemCount } = cartFeedback || {};
  const itemQuantity = item?.quantity || 1;
  const lineTotal = (item?.price ?? 0) * itemQuantity;
  const cartCount = getCartItemCount();
  const subtotal = getCartSubtotal();

  const handleClose = useCallback(() => {
    closeCartAddedFeedback?.();
  }, [closeCartAddedFeedback]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') handleClose();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  const title = message || t('cart.addedToCartTitle', 'Sepete eklendi!');
  const subtitle = itemCount && itemCount > 1
    ? t('cart.addedMultipleSubtitle', '{{count}} ürün sepetinize eklendi', { count: itemCount })
    : t('cart.addedToCartSubtitle', 'Ürün sepetinize başarıyla eklendi.');

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex justify-end" role="dialog" aria-modal="true" aria-labelledby="cart-added-title">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/45 backdrop-blur-[2px] transition-opacity"
        onClick={handleClose}
        aria-label={t('cart.closeDrawer', 'Kapat')}
      />

      <div
        className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl animate-[slideInRight_0.35s_ease-out] sm:rounded-l-2xl"
        style={{
          animation: 'slideInRight 0.35s ease-out',
        }}
      >
        <div className="border-b border-slate-100 bg-gradient-to-br from-emerald-50 via-white to-white px-5 py-5 sm:px-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-500/30">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h2 id="cart-added-title" className="text-lg font-semibold text-slate-900">
                  {title}
                </h2>
                <p className="mt-0.5 text-sm text-slate-600">{subtitle}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
              aria-label={t('cart.closeDrawer', 'Kapat')}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {item && (
          <div className="border-b border-slate-100 px-5 py-4 sm:px-6">
            <div className="flex gap-3 rounded-xl border border-slate-100 bg-slate-50/80 p-3">
              <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg bg-white">
                <img
                  src={item.image || '/assets/images/placeholder.jpg'}
                  alt={item.name}
                  className="h-full w-full object-contain p-1"
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2 text-sm font-medium text-slate-900">{item.name}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {t('cart.quantity')}: {itemQuantity}
                </p>
                <p className="mt-2 text-sm font-semibold text-slate-900">
                  {formatCurrency(lineTotal)}
                  <span className="ml-1 text-xs font-normal text-slate-500">+ KDV</span>
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4 sm:px-6">
          <div className="rounded-xl border border-slate-100 bg-white p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">{t('cart.itemsInCart', 'Sepetteki ürün')}</span>
              <span className="font-semibold text-slate-900">{cartCount}</span>
            </div>
            <div className="mt-2 flex items-center justify-between text-sm">
              <span className="text-slate-600">{t('cart.subtotal')}</span>
              <span className="font-semibold text-slate-900">{formatCurrency(subtotal)}</span>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-100 bg-white px-5 py-4 sm:px-6">
          <div className="flex flex-col gap-2.5">
            <Link
              to="/cart"
              onClick={handleClose}
              className="flex w-full items-center justify-center rounded-xl bg-slate-900 px-4 py-3.5 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              {t('cart.viewCart', 'Sepeti Görüntüle')}
            </Link>
            <button
              type="button"
              onClick={handleClose}
              className="flex w-full items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              {t('cart.continueShopping', 'Alışverişe Devam Et')}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0.6; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default CartAddedDrawer;
